import { useMemo, useState, type FormEvent } from 'react'
import { Link } from 'react-router'
import { Button, Field, Icon, Stack, Text } from '@/design-system'
import { FlowLayout } from '@/app/layouts/FlowLayout'
import { useAuth } from '@/app/auth/auth-context'
import { SocialRow, OrDivider } from '@/features/auth/AuthForm'
import { cn } from '@/lib/cn'
import { STEP_NUMBER, TOTAL_STEPS, saveDraft, useOnboardingDraft } from './progress'
import styles from './Onboarding.module.css'

/** Four checks, one bar each: length, case, a digit, a symbol. */
function strength(password: string) {
  const checks = [
    password.length >= 10,
    /[a-z]/.test(password) && /[A-Z]/.test(password),
    /\d/.test(password),
    /[^A-Za-z0-9]/.test(password),
  ]
  const score = checks.filter(Boolean).length
  const labels = ['Too short', 'Weak', 'Fair', 'Strong', 'Very strong']
  const hint = !checks[0]
    ? 'Use at least 10 characters'
    : !checks[1]
      ? 'Mix upper and lower case'
      : !checks[2]
        ? 'Add a number'
        : !checks[3]
          ? 'Add one symbol for the highest score'
          : 'Hard to guess'
  return { score, label: labels[score] ?? '', hint, ok: score >= 3 }
}

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/

/** 05 · Step 1 of 5 — create the account. */
export function OnboardingCreate() {
  const { signUp } = useAuth()
  const [draft] = useOnboardingDraft()
  const [name, setName] = useState(draft.name)
  const [email, setEmail] = useState(draft.email)
  const [phone, setPhone] = useState(draft.phone)
  const [password, setPassword] = useState('')
  const [agreed, setAgreed] = useState(false)
  const [touched, setTouched] = useState(false)
  const [submitting, setSubmitting] = useState(false)

  const pw = useMemo(() => strength(password), [password])
  const valid = {
    name: name.trim().split(/\s+/).length >= 2,
    email: EMAIL.test(email.trim()),
    phone: phone.replace(/\D/g, '').length >= 8,
    password: pw.ok,
  }
  const ready = Object.values(valid).every(Boolean) && agreed

  const submit = async (event: FormEvent) => {
    event.preventDefault()
    setTouched(true)
    if (!ready) return
    setSubmitting(true)
    // Record the next step before signing in: the guard reads it the moment
    // the session exists and sends the person on, so nothing here navigates.
    saveDraft({ name: name.trim(), email: email.trim(), phone: phone.trim(), step: 'verify' })
    await signUp({ email: email.trim(), password })
  }

  return (
    <FlowLayout
      step={STEP_NUMBER.create}
      totalSteps={TOTAL_STEPS}
      back="/onboarding"
      footer={
        <>
          <Button size="lg" block type="submit" form="create-account" disabled={submitting}>
            {submitting ? 'Creating your account…' : 'Create account'}
          </Button>
          <Text variant="caption" tone="secondary">
            Already have an account? <Link to="/login">Log in</Link>
          </Text>
        </>
      }
    >
      <form id="create-account" onSubmit={submit} noValidate>
        <Stack gap={6}>
          <Stack gap={2}>
            <Text as="h1" variant="title" weight="bold">
              Create your account
            </Text>
            <Text variant="body" tone="secondary">
              Two minutes and no paperwork. We only ask for what the regulator requires.
            </Text>
          </Stack>

          <Stack gap={4}>
            <Field
              label="Full name"
              autoComplete="name"
              value={name}
              onChange={(event) => setName(event.target.value)}
              {...(touched && !valid.name ? { error: 'Your first and last name, as on your ID.' } : {})}
            />
            <Field
              label="Email"
              type="email"
              autoComplete="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              {...(touched && !valid.email ? { error: 'That does not look like an email address.' } : {})}
            />
            <Field
              label="Phone"
              type="tel"
              autoComplete="tel"
              placeholder="+1 347 555 0182"
              value={phone}
              onChange={(event) => setPhone(event.target.value)}
              {...(touched && !valid.phone ? { error: 'Include the country code.' } : {})}
            />
            <Stack gap={2}>
              <Field
                label="Password"
                type="password"
                autoComplete="new-password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                {...(touched && !valid.password ? { error: 'Pick a stronger password.' } : {})}
              />
              {password && (
                <>
                  <div className={styles.strength} aria-hidden="true">
                    {[0, 1, 2, 3].map((i) => (
                      <span
                        key={i}
                        className={cn(
                          styles.bar,
                          i < pw.score && (pw.ok ? styles.barOn : styles.barWeak),
                        )}
                      />
                    ))}
                  </div>
                  <Text variant="caption" tone="secondary" aria-live="polite">
                    {pw.label} — {pw.hint}
                  </Text>
                </>
              )}
            </Stack>

            <button
              type="button"
              className={styles.check}
              onClick={() => setAgreed((value) => !value)}
              aria-pressed={agreed}
            >
              <Icon name={agreed ? 'check_box' : 'check_box_outline_blank'} size={22} filled={agreed} />
              <Text variant="caption">
                I agree to the <Link to="/terms">Terms</Link> and the{' '}
                <Link to="/privacy">Privacy Policy</Link>.
              </Text>
            </button>
            {touched && !agreed && (
              <Text variant="caption" tone="negative" weight="semibold">
                You need to accept the terms to open an account.
              </Text>
            )}
          </Stack>

          <OrDivider />
          <SocialRow />
        </Stack>
      </form>
    </FlowLayout>
  )
}
